import { getCaret, isSpacer, setCaret } from './coder.helpers.mjs';
import { astToDOM, domToAST, SPACER } from '../helpers/iml.mjs';
import { I18n } from '../helpers/i18n.mjs';
import { registerCustomElement } from '../utils.mjs';
import { IML } from '../deps/iml.mjs';

import './aiBlock.scss';

export const MAPAI_FUNCTION_NAME = 'mapAi';
export const MAPAI_PINNED_KEY = 'internal-internal-mapAi';
export const MAPAI_FUNCTION_LABEL = I18n.l('aiBlock.label');

export const AI_BLOCK_SELECTOR = 'imt-ai-block';

export class AiBlock extends HTMLElement {
	connectedCallback() {
		this.setAttribute('contenteditable', 'true');
		this.setAttribute('focused-placeholder', I18n.l('aiBlock.focusedPlaceholder'));

		this.boundKeydownHandler = this.handleKeyDown.bind(this);
		this.coder = this.closest('imt-coder');
		this.coder.addEventListener('keydown', this.boundKeydownHandler);

		this.boundInputHandler = this.handleInput.bind(this);
		this.coder.addEventListener('input', this.boundInputHandler);
		this.boundInputHandler();
	}

	get pills() {
		return this.coder.pills;
	}

	handleInput() {
		const needsGap = (node, secondNode) => {
			return (
				((isSpacer(node) || node?.textContent === '') && secondNode?.nodeType === 1) ||
				(node && (!!node?.wholeText?.length || node.nodeType === 1))
			);
		};

		this.classList.toggle('top-gap', needsGap(this.previousSibling, this.previousSibling?.previousSibling));
		this.classList.toggle('bottom-gap', needsGap(this.nextSibling, this.nextSibling?.nextSibling));
	}

	handleKeyDown(e) {
		if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
			this.isCaretAtBoundary(() => {
				const spacer = document.createTextNode(SPACER);
				if (e.key === 'ArrowDown') {
					this.after(spacer);
				} else {
					this.before(spacer);
				}
				setCaret(spacer, 0);
				this.handleInput();
			});
		}
	}

	disconnectedCallback() {
		this.coder.removeEventListener('keydown', this.boundKeydownHandler);
		this.coder.removeEventListener('input', this.boundInputHandler);
	}

	isNodeWithinComponent(node) {
		let container = node;
		while (container && container.nodeType !== 1) {
			container = container.parentNode;
		}
		return container === this;
	}

	containsOnlyZeroWidthSpaces(textNode, startOffset, endOffset) {
		const textBetween = textNode.textContent.substring(
			Math.min(startOffset, endOffset),
			Math.max(startOffset, endOffset),
		);
		return textBetween.replaceAll(SPACER, '').length === 0;
	}

	defer(callback) {
		return requestAnimationFrame(callback);
	}

	isCaretAtBoundary(onSuccess) {
		const previousPosition = getCaret();
		if (!previousPosition.node || !this.isNodeWithinComponent(previousPosition.node)) {
			return;
		}

		// we need to let the default caret behavior happen
		this.defer(() => {
			const newPosition = getCaret();
			if (!newPosition.node || !this.isNodeWithinComponent(newPosition.node)) {
				return;
			}

			// case 1: we are at the exact same position
			if (previousPosition.node === newPosition.node && previousPosition.offset === newPosition.offset) {
				onSuccess();
			}

			// case 2: we changed position but we are still within component and could've moved just by SPACER
			// this covers two options, one where we are at the end of the ai-block and the second is if we are at new line
			if (
				(previousPosition.node.parentNode === newPosition.node || previousPosition.node === newPosition.node) &&
				previousPosition.node.nodeType === 3
			) {
				if (this.containsOnlyZeroWidthSpaces(previousPosition.node, previousPosition.offset, newPosition.offset)) {
					onSuccess();
				}
			}
		});
	}
}

/**
 * Check if the node is an AI block element.
 * @param [node] {Node} node to check
 */
export function isAiBlock(node) {
	return node?.nodeName?.toLowerCase() === AI_BLOCK_SELECTOR;
}

/**
 * Create new AI block element.
 * @param [children] {Node[]} optional children nodes to be copied into the new AI block
 */
export function createAiBlock(children) {
	const element = document.createElement(AI_BLOCK_SELECTOR);

	if (!children || !children.length) {
		element.appendChild(document.createTextNode(SPACER));
		return element;
	}

	for (const child of children) {
		element.appendChild(child.cloneNode(true));
	}

	return element;
}

function removeAiBlockFromDom(dom) {
	return dom.flatMap((item) => (isAiBlock(item) ? [...item.childNodes] : [item]));
}

/**
 * Handle paste event for AI block element.
 * @param text {string} Text to be pasted into an AI block
 * @param options {object}
 */
export function handleAiBlockPaste(text, { variables, domCache, imlItems }) {
	const ast = IML.parse(text, undefined, undefined, { keepUnwantedOperators: true });
	for (const error of IML.errors) console.warn(error);

	const dom = astToDOM(ast, variables, undefined, false, domCache, imlItems);

	return IML.stringify(domToAST(removeAiBlockFromDom(dom), true));
}

registerCustomElement(AI_BLOCK_SELECTOR, AiBlock);
